import { salons } from "@/data/salons";
import { staff } from "@/data/barbers";
import type { Salon, Service, Guide, OpeningHour, Review } from "@/data/types";
import logoBlack from "@/assets/logo-black.png.asset.json";
import { ratingFor, reviewCountFor } from "@/data/reviewStats";


export const SITE_URL = "https://tailorsbarber.se";

const DAY_SCHEMA: Record<OpeningHour["day"], string> = {
  Mån: "Monday",
  Tis: "Tuesday",
  Ons: "Wednesday",
  Tor: "Thursday",
  Fre: "Friday",
  Lör: "Saturday",
  Sön: "Sunday",
};

function parseHours(hours: string): { opens?: string; closes?: string } {
  const m = hours.match(/(\d{2}:\d{2})\s*[–-]\s*(\d{2}:\d{2})/);
  if (!m) return {};
  return { opens: m[1], closes: m[2] };
}

// Canonical URL form: trailing slash on every path except the root's bare
// domain form and static files (last segment contains a dot).
function withTrailingSlash(pathname: string): string {
  if (pathname === "" || pathname === "/") return "/";
  const last = pathname.split("/").pop() ?? "";
  if (last.includes(".")) return pathname;
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function abs(path: string) {
  if (!path) return `${SITE_URL}/`;
  if (path.startsWith("http")) {
    try {
      const u = new URL(path);
      u.pathname = withTrailingSlash(u.pathname);
      return u.toString();
    } catch {
      return path;
    }
  }
  const clean = `/${path.replace(/^\/+/, "")}`;
  return `${SITE_URL}${withTrailingSlash(clean)}`;
}

export function absUrl(path: string) {
  return abs(path);
}

export function languageAlternates(svPath: string, enPath: string) {
  return [
    { rel: "alternate", hrefLang: "sv", href: abs(svPath) },
    { rel: "alternate", hrefLang: "en", href: abs(enPath) },
    { rel: "alternate", hrefLang: "x-default", href: abs(svPath) },
  ];
}


export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "Tailor's Barber",
    url: SITE_URL,
    logo: logoBlack.url,
    description:
      "Tailor's Barber är en premium barbersalong med fyra salonger i Stockholm. Specialiserade på herrklippning, taper fade, skin fade och skäggtrimning.",
    sameAs: [
      "https://www.instagram.com/tailorsbarber/",
      "https://www.facebook.com/tailorsbarber/",
      "https://www.tiktok.com/@tailorsbarber.se",
      "https://www.youtube.com/channel/UCjh8_8e4PmTZ3JD3r9wTckA",
      "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678",
    ],
    subOrganization: [
      {
        "@type": ["LocalBusiness", "BarberShop"],
        name: "Tailor's Barber – Stockholm City (Åhléns)",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Klarabergsgatan 50",
          postalCode: "111 21",
          addressLocality: "Stockholm",
          addressCountry: "SE",
        },
        url: abs("/barberare-stockholm-ahlens-city"),
        sameAs: ["https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678"],
      },
      {
        "@type": ["LocalBusiness", "BarberShop"],
        name: "Tailor's Barber – Täby Centrum",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Biblioteksgången 1",
          postalCode: "183 34",
          addressLocality: "Täby",
          addressCountry: "SE",
        },
        url: abs("/barberare-stockholm-taby"),
        sameAs: ["https://www.bokadirekt.se/places/tailors-barber-taby-centrum-37677"],
      },
      {
        "@type": ["LocalBusiness", "BarberShop"],
        name: "Tailor's Barber – Åkersberga",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Långhundravägen 9",
          postalCode: "184 44",
          addressLocality: "Åkersberga",
          addressCountry: "SE",
        },
        url: abs("/barberare-stockholm-akersberga"),
        sameAs: ["https://www.bokadirekt.se/places/tailors-barber-akersberga-ica-maxi-huset--37682"],
      },
      {
        "@type": ["LocalBusiness", "BarberShop"],
        name: "Tailor's Barber – Arninge",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Kundvägen 1D",
          postalCode: "187 66",
          addressLocality: "Täby",
          addressCountry: "SE",
        },
        url: abs("/barberare-stockholm-arninge"),
        sameAs: ["https://www.bokadirekt.se/places/tailors-barber-arninge-ica-maxi-huset--61598"],
      },
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Tailor's Barber",
    url: SITE_URL,
  };
}

export function reviewSchema(review: Review, salon?: Salon) {
  return {
    "@context": "https://schema.org",
    "@type": "Review",
    author: { "@type": "Person", name: review.author },
    datePublished: review.date,
    reviewBody: review.text,
    reviewRating: {
      "@type": "Rating",
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1,
    },
    ...(salon
      ? {
          itemReviewed: {
            "@type": "BarberShop",
            name: salon.name,
            url: abs(salon.url),
            address: {
              "@type": "PostalAddress",
              streetAddress: salon.address,
              postalCode: salon.postal,
              addressLocality: salon.city,
              addressCountry: "SE",
            },
          },
        }
      : {}),
    publisher: { "@type": "Organization", name: review.source },
  };
}

export function localBusinessSchema(
  salon: Salon,
  reviews: Review[] = [],
  translated?: { name?: string; description?: string },
) {
  const shown = reviews
    .filter((r) => r.salonSlug === salon.slug && r.rating === 5)
    .slice(0, 5);
  // Språk per barberare i den strukturerade datan: employee med knowsLanguage
  // (standard schema.org-egenskaper). Endast barberare med uppgifter tas med.
  const speakingStaff = staff.flatMap((s) =>
    s.at[salon.slug] && s.languages?.length
      ? [{ name: s.name, languages: s.languages }]
      : [],
  );
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "BarberShop"],
    "@id": abs(salon.url),
    name: translated?.name ?? salon.name,
    ...(translated?.description ? { description: translated.description } : {}),
    url: abs(salon.url),
    telephone: salon.phone,
    priceRange: salon.priceRange,
    currenciesAccepted: "SEK",
    paymentAccepted: "Cash, Credit Card, Swish",
    sameAs: [salon.bokaDirektUrl],
    ...(speakingStaff.length > 0
      ? {
          employee: speakingStaff.map((s) => ({
            "@type": "Person",
            name: s.name,
            knowsLanguage: s.languages,
          })),
        }
      : {}),
    address: {
      "@type": "PostalAddress",
      streetAddress: salon.address,
      postalCode: salon.postal,
      addressLocality: salon.city,
      addressCountry: "SE",
    },
    geo: { "@type": "GeoCoordinates", latitude: salon.lat, longitude: salon.lng },
    openingHoursSpecification: salon.openingHours.map((h) => {
      const { opens, closes } = parseHours(h.hours);
      return {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: DAY_SCHEMA[h.day],
        ...(opens ? { opens } : {}),
        ...(closes ? { closes } : {}),
      };
    }),
    aggregateRating: {
      "@type": "AggregateRating",
      name: "Google",
      ratingValue: ratingFor(salon.slug),
      reviewCount: reviewCountFor(salon.slug),
      ratingCount: reviewCountFor(salon.slug),
      bestRating: 5,
      worstRating: 1,
    },
    ...(shown.length > 0
      ? {
          review: shown.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.author },
            datePublished: r.date,
            reviewBody: r.text,
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
            publisher: { "@type": "Organization", name: r.source },
          })),
        }
      : {}),
    image: abs(salon.image),
  };
}

export function serviceSchema(service: Service) {
  const providerSalons = salons.filter((s) => service.availableAt.includes(s.slug));

  // NOTE: no aggregateRating/review here. Google only allows review snippets on
  // a fixed set of parent types, and Service is not one of them ("Invalid object
  // type for field <parent_node>"). Ratings live on localBusinessSchema()
  // (LocalBusiness/BarberShop) with real per-salon data — that is what produces
  // the stars in search. A derived cross-salon average on a Service node is both
  // invalid markup and self-serving review markup.
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.excerpt,
    provider: { "@type": "Organization", name: "Tailor's Barber", url: SITE_URL },
    areaServed: "Stockholm",
    ...(service.priceFrom
      ? {
          offers: {
            "@type": "Offer",
            price: service.priceFrom,
            priceCurrency: "SEK",
            availability: "https://schema.org/InStock",
          },
        }
      : {}),

    availableChannel: providerSalons.map((s) => ({
      "@type": "ServiceChannel",
      serviceUrl: abs(s.url),
    })),
  };
}


export function faqSchema(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.url),
    })),
  };
}

export function articleSchema(guide: Guide) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    datePublished: guide.publishedAt,
    author: { "@type": "Organization", name: "Tailor's Barber", url: SITE_URL },
    description: guide.excerpt,
  };
}

function ldId(json: string): string {
  let h = 5381;
  for (let i = 0; i < json.length; i++) h = ((h << 5) + h + json.charCodeAt(i)) | 0;
  return `ld-${(h >>> 0).toString(36)}`;
}

export function jsonLd<T>(data: T, id?: string) {
  const json = JSON.stringify(data);
  return { type: "application/ld+json", id: id ?? ldId(json), children: json };
}
